import {
  ComponentProps,
  ForwardRefExoticComponent,
  KeyboardEvent,
  RefAttributes,
  SVGProps,
} from "react";
import {
  RegisterOptions,
  useController,
  useFormContext,
} from "react-hook-form";
import { Icon, Text } from "@ttc3k/trekker";
import { Box, Flex } from "styled-system/jsx";
import { Input } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";

export type ControlledInputWithIconProps = ComponentProps<typeof Input> & {
  IconElement: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  iconClassName?: string;
  iconPlacement?: "left" | "right";
  name: string;
  registerOptions?: RegisterOptions;
  onPressEnterToSearch?: (value: string) => void;
};

export const ControlledInputWithIcon = ({
  IconElement,
  iconClassName,
  iconPlacement = "right",
  name,
  hint,
  registerOptions,
  onPressEnterToSearch,
  ...rest
}: ControlledInputWithIconProps) => {
  const { t } = useTranslation("core");
  const { register, control } = useFormContext();
  const { fieldState } = useController({ name, control });

  const props = {
    ...rest,
    bg: "white",
    ...register(name, registerOptions),
    hint: fieldState.error?.message ?? hint,
    ...(fieldState.error ? { "data-invalid": true, "aria-invalid": true } : {}),
  };

  return (
    // NOTE: I tried to use the InputWithIcon here, but it threw an error
    <Box w={"full"} position={"relative"}>
      <Input
        pl={
          iconPlacement === "left"
            ? onPressEnterToSearch
              ? "1000"
              : "400"
            : "150"
        }
        pr={
          iconPlacement === "right"
            ? onPressEnterToSearch
              ? "1000"
              : "400"
            : "150"
        }
        {...(onPressEnterToSearch && {
          onKeyUp: (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              onPressEnterToSearch?.(props.value as string);
            }
          },
        })}
        {...props}
      />
      <Flex
        position={"absolute"}
        top={props.label && props.label.length > 0 ? "38px" : "16px"}
        gap={"50"}
        right={iconPlacement === "right" ? "150" : "unset"}
        left={iconPlacement === "left" ? "150" : "unset"}
        bgColor="white"
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {!!onPressEnterToSearch && (
          <Text visual={"smallTag"} color={"text.light"} userSelect="none">
            {t("core:ENTER")}
          </Text>
        )}
        <Icon
          Element={IconElement}
          size={"sm"}
          color={"icon.light"}
          strokeWidth={1.75}
          className={iconClassName}
        />
      </Flex>
    </Box>
  );
};
